import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  SIGNED_OUT,
  canSeeStrategistPayout,
  canUsePipeline,
  hasAccess,
  isAdminRole,
  studioAccess,
  type SessionInfo,
  type ViewAsRequest,
} from "@scandi/shared";
import { api, isUnauthorized } from "@/lib/api";
import { useFailureToast } from "@/hooks/use-failure-toast";
import { signInWithGoogle } from "@/features/auth/firebase";
import { clearCommentDrafts } from "@/features/pipeline/chat/comment-drafts";
import { clearKeptDrafts } from "@/features/storyboard/kept-drafts";
import { appForPath } from "@/components/layout/apps-nav";
import { clearPersistedQueries } from "@/lib/persist-cache";

export const sessionKey = ["session"] as const;

/**
 * Who this tab last had signed in: the account's id, or the role for a shared
 * team login. Kept beyond a session that expires or is revoked elsewhere, which
 * flips the session to signed out without a sign-out of ours, so the next
 * sign-in can tell whether it is the same person coming back.
 */
let lastIdentity: string | null = null;

function identityOf(session: SessionInfo | undefined): string | null {
  if (!session?.authenticated) return null;
  // The real person, never a persona being viewed as.
  return session.user && !session.viewAs ? `user:${session.user.id}` : `role:${session.realRole ?? session.role ?? "none"}`;
}

export function useSession() {
  const query = useQuery({
    queryKey: sessionKey,
    queryFn: api.session,
    // Sessions last months; re-checking on every window focus is pure noise. A
    // revoked or demoted session is caught sooner than this anyway: any query
    // the server refuses for that reason re-reads the session (see main.tsx).
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const session = query.data;
  const identity = identityOf(session);
  if (identity && !session?.viewAs) lastIdentity = identity;
  const role = session?.role ?? null;
  const isAuthenticated = session?.authenticated ?? false;
  const user = session?.user ?? null;
  const viewAs = session?.viewAs ?? null;
  // Older API builds do not send realRole; without view-as it equals role.
  const realRole = session?.realRole ?? role;

  return {
    session,
    /**
     * The personal account behind the session; null for a team password. While
     * viewing as someone, the account linked to that person, if they have one.
     */
    user,
    /**
     * Who the platform is being shown as, or null. Everything else this hook
     * returns — role, user, the flags below — already describes that person;
     * this is for the banner, and for the way back out.
     */
    viewAs,
    /** The role of whoever actually signed in. */
    realRole,
    /**
     * Whether "Ver como…" is offered. Read from the real role, so it stays on
     * the menu while an admin is looking through an editor's eyes.
     */
    isRealAdmin: isAdminRole(realRole),
    /**
     * The editor and strategist this session works as. Normally the account's
     * own; while viewing as someone with no account, the persona's, which the
     * pipeline's rules need just the same.
     */
    editor: user?.editor ?? viewAs?.editor ?? null,
    strategist: user?.strategist ?? viewAs?.strategist ?? null,
    /** A name for greetings: the account's, else the persona's roster name. */
    displayName: user?.name ?? (viewAs?.label || null),
    method: session?.method ?? null,
    role,
    isLoading: query.isLoading,
    /** Re-reads the session, for the "check again" button on the waiting screen. */
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    isAuthenticated,
    hasAccess: isAuthenticated && hasAccess(role),
    /**
     * Signed in, but nobody has granted this account a role yet. The app shows
     * only the waiting screen: every data endpoint would refuse it anyway, and a
     * page of red errors reads as broken rather than as "not yet".
     */
    isPending: isAuthenticated && !hasAccess(role),
    isAdmin: isAdminRole(role),
    isEditor: role === "editor",
    isStrategist: role === "strategist",
    /**
     * Who the strategist's payout is shown to: the strategist herself, and the
     * admins who agreed the terms. Editors are not in that conversation.
     */
    canSeeStrategist: canSeeStrategistPayout(role),
    /**
     * Whether the creative pipeline exists for this role: briefs, decks,
     * resources, the bell. False for a results-only `user`, whom the API
     * refuses there anyway.
     */
    canUsePipeline: canUsePipeline(role),
    /**
     * How much of the studio this session has: `full` for strategists and
     * admins, `script_chat` for a user or editor an admin gave the "Script
     * chat" add-on (their own chats, nothing that starts an ad job or
     * publishes), null for none. The persona's while viewing as someone, as
     * the API decides it.
     */
    studio: studioAccess(role, user?.scriptChat === true),
    /**
     * The separate apps this person was given (the sidebar's "Apps" group).
     * Decided by the server for the real person, never while viewing as someone.
     */
    apps: session?.apps ?? [],
  };
}

/**
 * The Studio job form's drafts (`features/studio/brief-form/form-store.ts`,
 * `scandi.studio.brief-form:<…>`), kept per account in this browser. Inline
 * rather than imported, so this hook (in the app's main chunk) never pulls in
 * the form and the brief's schemas, which load with their own route.
 */
function clearStudioFormDrafts(): void {
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key?.startsWith("scandi.studio.brief-form:")) window.localStorage.removeItem(key);
    }
  } catch {
    // No storage: nothing kept.
  }
}

/**
 * After signing in, the session is known and everything read before it is stale.
 *
 * Somebody else than whoever this tab last had (their session expired or was
 * revoked, and the next person signed in without a reload) gets none of the
 * previous person's reads, kept drafts or unsent comments: the same clean-up
 * as a sign-out. The same person coming back keeps their drafts.
 */
function useAdoptSession() {
  const queryClient = useQueryClient();
  return useCallback(
    (session: SessionInfo) => {
      const next = identityOf(session);
      const switched = lastIdentity !== null && next !== lastIdentity;
      lastIdentity = next;
      queryClient.setQueryData(sessionKey, session);
      if (switched) {
        clearPersistedQueries();
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
        clearKeptDrafts();
        clearCommentDrafts();
        clearStudioFormDrafts();
        return;
      }
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== "session" });
    },
    [queryClient],
  );
}

export function useLogin() {
  const adopt = useAdoptSession();
  return useMutation({ mutationFn: api.login, onSuccess: adopt });
}

/**
 * Google sign-in, start to finish: the popup, then trading its token for our own
 * session cookie.
 *
 * Resolves to null when the person closed the popup themselves. That is a change
 * of mind, not a failure, and deserves no error message.
 */
export function useGoogleSignIn() {
  const adopt = useAdoptSession();
  return useMutation({
    mutationFn: signInWithGoogle,
    onSuccess: (session) => {
      if (!session) return;
      // Someone who opened one of their separate apps while signed out lands in
      // it now rather than on this app's home: it is another build, so reload.
      if (appForPath(session.apps, window.location.pathname)) {
        window.location.reload();
        return;
      }
      adopt(session);
    },
  });
}

/**
 * Forget the signed-in state locally.
 *
 * Flips the session first so the app swaps to the login screen and the
 * authenticated views unmount. Deliberately not queryClient.clear(): that
 * detaches live observers from the cache, so the mounted session query never
 * sees the value written afterwards and the user is left staring at a stale
 * leaderboard.
 *
 * Then drops everything else, the presentation editor's kept drafts with it.
 * None of it is readable by whoever uses this browser next.
 */
function signOutLocally(queryClient: QueryClient) {
  lastIdentity = null;
  clearPersistedQueries();
  queryClient.setQueryData<SessionInfo>(sessionKey, SIGNED_OUT);
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
  // Unsaved words of the presentation editor, kept in this browser for a week.
  clearKeptDrafts();
  // Unsent comments, kept while their sheet is closed.
  clearCommentDrafts();
  // The Studio job form's draft, kept per account.
  clearStudioFormDrafts();
}

/**
 * What a failed sign-out means. A 401 says the server already considers this
 * browser signed out (revoked from another device, or expired), which is the
 * outcome that was asked for, so the local state follows. Anything else — the
 * network, a deploy restarting the API — leaves the session alive on the
 * server, so the app stays signed in and says the sign-out did not happen
 * rather than pretending it did.
 */
function useSignOutFailure() {
  const queryClient = useQueryClient();
  const failed = useFailureToast();
  return useCallback(
    (error: unknown) => {
      if (isUnauthorized(error)) signOutLocally(queryClient);
      else failed(error);
    },
    [queryClient, failed],
  );
}

export function useLogout() {
  const queryClient = useQueryClient();
  const onError = useSignOutFailure();
  return useMutation({
    mutationFn: api.logout,
    onSuccess: () => signOutLocally(queryClient),
    onError,
  });
}

/**
 * Signs a personal account out on every device, this one included: for a lost
 * laptop, or a phone left signed in somewhere. Team-password sessions belong to
 * nobody, so the server refuses them.
 */
export function useLogoutEverywhere() {
  const queryClient = useQueryClient();
  const onError = useSignOutFailure();
  return useMutation({
    mutationFn: api.logoutEverywhere,
    onSuccess: () => signOutLocally(queryClient),
    onError,
  });
}

/* ---------------------------------------------------------------- view as */

/**
 * Swap everything the app knows for what the new identity would see.
 *
 * The session is written first, so the shell re-renders for the new role at
 * once. Then every other query goes: inactive ones are dropped outright, and
 * the ones on screen are reset — cleared and refetched — rather than removed,
 * because a removed query keeps its mounted observers pointing at a cache
 * entry that no longer exists, and the page would sit on the old person's
 * data. Nothing fetched as one person may be shown as another's.
 */
function useSwapIdentity() {
  const queryClient = useQueryClient();
  return useCallback(
    (session: SessionInfo) => {
      queryClient.setQueryData(sessionKey, session);
      clearPersistedQueries();
      // Words typed as one person are not the next one's to send.
      clearCommentDrafts();
      const others = (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== "session";
      queryClient.removeQueries({ predicate: others, type: "inactive" });
      void queryClient.resetQueries({ predicate: others });
    },
    [queryClient],
  );
}

/**
 * Start viewing the platform as someone. Lands on the page that person opens
 * the app to — their own work for an editor or strategist, the leaderboard for
 * a plain user — rather than leaving the admin on an admin page that is now,
 * correctly, refused.
 */
export function useStartViewAs() {
  const swap = useSwapIdentity();
  const navigate = useNavigate();
  const failed = useFailureToast();
  return useMutation({
    mutationFn: (persona: ViewAsRequest) => api.startViewAs(persona),
    onSuccess: (session, persona) => {
      swap(session);
      navigate(persona.role === "user" ? "/" : "/pipeline");
    },
    onError: (error) => failed(error),
  });
}

/** Back to the admin's own view, on whatever page they were looking at. */
export function useStopViewAs() {
  const swap = useSwapIdentity();
  const failed = useFailureToast();
  return useMutation({
    mutationFn: api.stopViewAs,
    onSuccess: swap,
    onError: (error) => failed(error),
  });
}
